import { Alert, Divider, Icons, Stack } from '@healthinal/ui';
import { ReactNode } from 'react';
import { HeurekaMedicationStatement, PatientWithHeurekaData } from '../api/heurekaPatientDetails';
import { formatDate } from '../utils/date';
import { ConditionsSection } from './ConditionSection';
import { DataItem } from './DataItem';
import MedicationTable from './MedicationTable';
import { PracticeEmailDataItem } from './PracticeEmailDataItem';

interface Props {
  patientData: PatientWithHeurekaData;
  headerAction?: ReactNode;
  additionalDataItem?: ReactNode;
  showConditions?: boolean;
  showPracticeEmail?: boolean;
}

export default function HeurekaPatient({
  patientData,
  headerAction,
  additionalDataItem,
  showConditions = false,
  showPracticeEmail = false,
}: Props) {
  const { practiceName, practiceEmail, insuranceName, insurancePatientNumber, heurekaDetails } = patientData;

  if (heurekaDetails == null) {
    return null;
  }

  const regularMedication: HeurekaMedicationStatement[] = [];
  const asNeededMedication: HeurekaMedicationStatement[] = [];
  heurekaDetails.medicationStatements.forEach((medication) => {
    if (medication.asNeeded) {
      asNeededMedication.push(medication);
    } else {
      regularMedication.push(medication);
    }
  });

  return (
    <>
      <Alert variant="soft" startDecorator={<Icons.ErrorOutline />} sx={{ my: 3 }}>
        Achtung diese Daten können unvollständig, fehlerhaft oder nicht mehr aktuell sein und müssen deshalb vor
        Verwendung mit dem Patienten validiert werden. Der Urheber der Informationen sowie der Betreiber des Dashboards
        übernehmen keine Haftung für die Vollständigkeit oder Richtigkeit der Daten.
      </Alert>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Stack direction="row" gap={6}>
          <DataItem label="Patient">
            {heurekaDetails.patient.firstName} {heurekaDetails.patient.lastName}
          </DataItem>
          <DataItem label="Geburtsdatum">{formatDate(heurekaDetails.patient.dateOfBirth)}</DataItem>
          <DataItem label="Patienten-ID">{heurekaDetails.patient.pid}</DataItem>
          <Divider orientation="vertical" />
          {additionalDataItem ? (
            additionalDataItem
          ) : (
            <SmcPatientDetails
              practiceName={practiceName}
              practiceEmail={showPracticeEmail ? practiceEmail : null}
              insuranceName={insuranceName}
              insurancePatientNumber={insurancePatientNumber}
            />
          )}
        </Stack>
        {headerAction}
      </Stack>

      {heurekaDetails.medicationStatements.length === 0 && (
        <Alert variant="soft" startDecorator={<Icons.InfoOutlined />} sx={{ mb: 3 }}>
          Keine Medikamente gefunden
        </Alert>
      )}

      {regularMedication.length > 0 && <MedicationTable medicationStatements={regularMedication} title="Medikation" />}
      {asNeededMedication.length > 0 && (
        <MedicationTable medicationStatements={asNeededMedication} title="Reservemedikation" />
      )}
      {showConditions && <ConditionsSection conditions={heurekaDetails.conditions} />}
    </>
  );
}

interface SmcPatientDetailsProps extends Omit<PatientWithHeurekaData, 'heurekaDetails'> {}

function SmcPatientDetails({
  practiceName,
  practiceEmail,
  insurancePatientNumber,
  insuranceName,
}: SmcPatientDetailsProps) {
  return (
    <>
      {practiceName && <DataItem label="Praxis">{practiceName}</DataItem>}
      <PracticeEmailDataItem email={practiceEmail} />
      {insuranceName && <DataItem label="Versicherung">{insuranceName}</DataItem>}
      {insurancePatientNumber && <DataItem label="Versicherten-Nr.">{insurancePatientNumber}</DataItem>}
    </>
  );
}
